// apps/web/src/components/practice/QuestionRenderer.tsx
// 题目渲染组件 — 移动端刷题样式：题型标签、选项即时反馈、解析区

import { useEffect, useMemo, useState } from "react";
import type { QuestionDTO, QuestionPracticeStatsDTO, QuestionType } from "@examfast/shared-types";

interface Props {
  question: QuestionDTO;
  onSubmit?: (answer: unknown) => void;
  disabled?: boolean;
  showResult?: boolean;
  isCorrect?: boolean;
  correctAnswer?: string;
  explanation?: string | null;
  questionNumber?: number;
  fontSize?: "small" | "standard" | "large" | "xlarge" | "xxlarge" | "max";
  optionSpacing?: "standard" | "wide" | "xwide";
  shuffleOptions?: boolean;
  showUserAnswer?: boolean;
  userAnswerFallback?: string;
  showExplanationPanel?: boolean;
  historyStats?: QuestionPracticeStatsDTO;
}

export default function QuestionRenderer({
  question,
  onSubmit,
  disabled = false,
  showResult = false,
  isCorrect,
  correctAnswer: correctAnswerProp,
  explanation,
  questionNumber,
  fontSize = "standard",
  optionSpacing = "standard",
  shuffleOptions = false,
  showUserAnswer = true,
  userAnswerFallback = "-",
  showExplanationPanel = true,
  historyStats,
}: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedMulti, setSelectedMulti] = useState<string[]>([]);
  const [fillAnswers, setFillAnswers] = useState<string[]>([""]);
  const [essayAnswer, setEssayAnswer] = useState("");
  const [TFAnswer, setTFAnswer] = useState<boolean | null>(null);

  const options = question.options ?? [];
  const optionItems = useMemo(
    () => buildOptionItems(options, question.id, shuffleOptions),
    [options, question.id, shuffleOptions]
  );
  const correctValue = useMemo(
    () => parseCorrectAnswer(question.type, correctAnswerProp ?? question.correct_answer),
    [question.type, correctAnswerProp, question.correct_answer]
  );

  const fillBlanksCount = useMemo(() => {
    if (question.type !== "fill_blank") return 1;
    return Array.isArray(correctValue) ? correctValue.length : 1;
  }, [question.type, correctValue]);

  useEffect(() => {
    setSelected(null);
    setSelectedMulti([]);
    setFillAnswers(Array.from({ length: fillBlanksCount }, () => ""));
    setEssayAnswer("");
    setTFAnswer(null);
  }, [question.id, fillBlanksCount]);

  const canSubmit =
    !disabled &&
    ((question.type === "single_choice" && selected) ||
      (question.type === "multiple_choice" && selectedMulti.length > 0) ||
      (question.type === "true_false" && TFAnswer !== null) ||
      (question.type === "fill_blank" && fillAnswers.length === fillBlanksCount && fillAnswers.every((a) => a?.trim())) ||
      (question.type === "essay" && essayAnswer.trim().length > 0));

  const submit = (answer: unknown) => {
    if (disabled || showResult) return;
    onSubmit?.(answer);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (question.type === "single_choice") submit(selected);
    if (question.type === "multiple_choice") submit(selectedMulti);
    if (question.type === "true_false") submit(TFAnswer);
    if (question.type === "fill_blank") submit(fillAnswers.map((a) => a.trim()).filter(Boolean));
    if (question.type === "essay") submit(essayAnswer.trim());
  };

  return (
    <div className="space-y-5">
      <div className={`${fontSizeClass(fontSize)} font-semibold leading-[1.8] text-gray-900 whitespace-pre-wrap`}>
        <span className="mr-2 align-middle inline-flex px-1.5 py-0.5 rounded bg-blue-500 text-white text-xs font-medium leading-none">
          {typeLabel(question.type)}
        </span>
        {typeof questionNumber === "number" && <span>{questionNumber}、</span>}
        {question.content}
      </div>

      {question.type === "single_choice" && (
        <OptionList
          options={options}
          items={optionItems}
          selected={[selected].filter((v): v is string => !!v)}
          correctValue={correctValue}
          showResult={showResult}
          disabled={disabled}
          spacing={optionSpacing}
          onSelect={(letter) => {
            setSelected(letter);
            submit(letter);
          }}
        />
      )}

      {question.type === "multiple_choice" && (
        <>
          <OptionList
          options={options}
          items={optionItems}
            selected={selectedMulti}
            correctValue={correctValue}
            showResult={showResult}
            disabled={disabled}
            multiple
            spacing={optionSpacing}
            onSelect={(letter) => {
              if (disabled || showResult) return;
              setSelectedMulti((prev) =>
                prev.includes(letter) ? prev.filter((l) => l !== letter) : [...prev, letter]
              );
            }}
          />
          {!showResult && (
            <div className="flex items-center gap-5 pl-0.5">
              <button
                type="button"
                onClick={() => setSelectedMulti(options.map((_, idx) => String.fromCharCode(65 + idx)))}
                className="flex items-center gap-2 text-sm text-gray-500"
              >
                <span className="w-4 h-4 rounded-sm border border-gray-300 bg-white" />
                全选
              </button>
              <button
                disabled={!canSubmit}
                onClick={handleSubmit}
                className="px-9 py-2.5 rounded-full bg-blue-500 text-white text-sm font-semibold shadow-sm disabled:opacity-40"
              >
                提交答案
              </button>
            </div>
          )}
        </>
      )}

      {question.type === "true_false" && (
        <OptionList
          options={["正确", "错误"]}
          selected={TFAnswer === null ? [] : [TFAnswer ? "A" : "B"]}
          correctValue={correctValue === true ? "A" : correctValue === false ? "B" : correctValue}
          showResult={showResult}
          disabled={disabled}
          spacing={optionSpacing}
          onSelect={(letter) => {
            const answer = letter === "A";
            setTFAnswer(answer);
            submit(answer);
          }}
        />
      )}

      {question.type === "fill_blank" && (
        <div className="space-y-3">
          {Array.from({ length: fillBlanksCount }).map((_, idx) => (
            <input
              key={idx}
              type="text"
              disabled={disabled}
              value={fillAnswers[idx] ?? ""}
              onChange={(e) => {
                const next = [...fillAnswers];
                next[idx] = e.target.value;
                setFillAnswers(next);
              }}
              placeholder={`请填写第 ${idx + 1} 个空`}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          ))}
          {!showResult && (
            <button
              disabled={!canSubmit}
              onClick={handleSubmit}
              className="w-full py-3 rounded-full bg-blue-500 text-white font-semibold disabled:opacity-40"
            >
              提交答案
            </button>
          )}
        </div>
      )}

      {question.type === "essay" && (
        <div className="space-y-3">
          <textarea
            disabled={disabled || showResult}
            value={essayAnswer}
            onChange={(event) => setEssayAnswer(event.target.value)}
            placeholder="请输入你的答案"
            rows={6}
            className="w-full resize-y rounded-xl border border-gray-200 px-4 py-3 text-base leading-7 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50"
          />
          {!showResult && (
            <button
              type="button"
              disabled={!canSubmit}
              onClick={handleSubmit}
              className="w-full rounded-full bg-blue-500 py-3 font-semibold text-white disabled:opacity-40"
            >
              提交答案
            </button>
          )}
        </div>
      )}

      {showResult && (
        <ResultPanel
          question={question}
          isCorrect={isCorrect}
          correctValue={correctValue}
          userValue={getUserAnswerLabel(question.type, selected, selectedMulti, TFAnswer, fillAnswers, essayAnswer)}
          explanation={explanation}
          showUserAnswer={showUserAnswer}
          userAnswerFallback={userAnswerFallback}
          showExplanationPanel={showExplanationPanel}
          historyStats={historyStats}
        />
      )}
    </div>
  );
}

interface OptionListProps {
  options: string[];
  items?: OptionItem[];
  selected: string[];
  correctValue: unknown;
  showResult: boolean;
  disabled: boolean;
  multiple?: boolean;
  spacing?: "standard" | "wide" | "xwide";
  onSelect: (letter: string) => void;
}

interface OptionItem {
  text: string;
  originalLetter: string;
  displayLetter: string;
}

function OptionList({
  options,
  items,
  selected,
  correctValue,
  showResult,
  disabled,
  multiple = false,
  spacing = "standard",
  onSelect,
}: OptionListProps) {
  const correctLetters = getCorrectLetters(correctValue);
  const optionItems = items ?? options.map((text, idx) => ({
    text,
    originalLetter: String.fromCharCode(65 + idx),
    displayLetter: String.fromCharCode(65 + idx),
  }));

  return (
    <div className={optionSpacingClass(spacing)}>
      {optionItems.map((item) => {
        const isSelected = selected.includes(item.originalLetter);
        const isCorrectOpt = correctLetters.includes(item.originalLetter);
        const isWrongSelected = showResult && isSelected && !isCorrectOpt;
        const isRight = showResult && isCorrectOpt;

        return (
          <button
            type="button"
            key={item.originalLetter}
            disabled={disabled}
            onClick={() => onSelect(item.originalLetter)}
            className="w-full grid grid-cols-[32px_1fr] items-start gap-3 text-left disabled:opacity-100"
          >
            <span
              className={[
                "mt-0.5 flex h-7 w-7 items-center justify-center rounded-full border text-sm font-semibold",
                isRight
                  ? "border-green-500 bg-green-500 text-white"
                  : isWrongSelected
                  ? "border-red-500 bg-red-500 text-white"
                  : isSelected
                  ? "border-blue-500 bg-blue-50 text-blue-600"
                  : "border-gray-200 bg-white text-gray-400",
              ].join(" ")}
            >
              {isRight ? "✓" : isWrongSelected ? "×" : item.displayLetter}
            </span>
            <span
              className={[
                "min-w-0 pt-0.5 text-[16px] leading-7 font-medium",
                isRight ? "text-green-600" : isWrongSelected ? "text-red-500" : "text-gray-800",
              ].join(" ")}
            >
              {cleanOptionText(item.text, item.originalLetter)}
              {multiple && isSelected && !showResult ? (
                <span className="ml-2 text-xs text-blue-500">已选</span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ResultPanel({
  question,
  isCorrect,
  correctValue,
  userValue,
  explanation,
  showUserAnswer,
  userAnswerFallback,
  showExplanationPanel,
  historyStats,
}: {
  question: QuestionDTO;
  isCorrect?: boolean;
  correctValue: unknown;
  userValue: string;
  explanation?: string | null;
  showUserAnswer: boolean;
  userAnswerFallback: string;
  showExplanationPanel: boolean;
  historyStats?: QuestionPracticeStatsDTO;
}) {
  return (
    <div className="space-y-4 pt-1">
      <div className={`${showUserAnswer ? "grid-cols-2" : "grid-cols-1"} grid rounded-lg bg-gray-50 text-center text-sm font-semibold overflow-hidden`}>
        <div className="py-3 text-gray-700">
          正确答案 <span className="text-green-600">{formatAnswer(question.type, correctValue)}</span>
        </div>
        {showUserAnswer && (
          <div className="py-3 text-gray-700">
            您选择 <span className={isCorrect ? "text-green-600" : "text-red-500"}>{userValue || userAnswerFallback}</span>
          </div>
        )}
      </div>

      {showExplanationPanel && (
        <section className="pt-1">
          <div className="mb-3 flex items-center justify-center gap-3 text-base font-semibold text-gray-900">
            <span className="h-px flex-1 bg-gray-100" />
            试题详解
            <span className="h-px flex-1 bg-gray-100" />
          </div>
          <div className="space-y-4 text-sm">
            <div>
              <p className="mb-2 font-semibold text-blue-700">解析</p>
              <p className="leading-7 text-gray-500">{explanation || "暂无解析"}</p>
            </div>
            <div>
              <p className="mb-2 font-semibold text-blue-700">难度</p>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <span
                    key={idx}
                    className={idx < (question.difficulty ?? 3) ? "text-yellow-400" : "text-gray-200"}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>
            {historyStats && (
              <div>
                <p className="mb-2 font-semibold text-blue-700">统计</p>
                <p className="leading-7 text-gray-500">
                  本人作答{historyStats.total_count}次，答对{historyStats.correct_count}次，正确率
                  {historyStats.accuracy_rate.toFixed(2)}%
                </p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function parseCorrectAnswer(type: QuestionType, raw?: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    if (type === "true_false") return raw === "true";
    return raw;
  }
}

function getCorrectLetters(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === "string") return [value];
  return [];
}

function getUserAnswerLabel(
  type: QuestionType,
  selected: string | null,
  selectedMulti: string[],
  tf: boolean | null,
  fillAnswers: string[],
  essayAnswer: string
): string {
  if (type === "single_choice") return selected ?? "";
  if (type === "multiple_choice") return selectedMulti.join("、");
  if (type === "true_false") return tf === null ? "" : tf ? "A" : "B";
  if (type === "fill_blank") return fillAnswers.filter(Boolean).join(" / ");
  if (type === "essay") return essayAnswer.trim();
  return "";
}

function formatAnswer(type: QuestionType, value: unknown): string {
  if (type === "true_false") return value === true ? "A" : value === false ? "B" : String(value ?? "");
  if (Array.isArray(value)) return value.join("、");
  return String(value ?? "");
}

function cleanOptionText(option: string, letter: string): string {
  return option.replace(new RegExp(`^${letter}[.．、\\s]*`), "").trim();
}

function buildOptionItems(options: string[], questionId: string, shuffle: boolean): OptionItem[] {
  const items = options.map((text, idx) => ({
    text,
    originalLetter: String.fromCharCode(65 + idx),
    displayLetter: String.fromCharCode(65 + idx),
  }));
  if (!shuffle || items.length < 2) return items;

  const shuffled = [...items];
  let seed = 0;
  for (let i = 0; i < questionId.length; i++) {
    seed = (seed * 31 + questionId.charCodeAt(i)) >>> 0;
  }
  for (let i = shuffled.length - 1; i > 0; i--) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const j = seed % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.map((item, idx) => ({
    ...item,
    displayLetter: String.fromCharCode(65 + idx),
  }));
}

function typeLabel(type: QuestionType): string {
  const map: Record<QuestionType, string> = {
    single_choice: "单选题",
    multiple_choice: "多选题",
    true_false: "判断题",
    fill_blank: "填空题",
    essay: "简答题",
  };
  return map[type] ?? type;
}

function fontSizeClass(size: NonNullable<Props["fontSize"]>): string {
  const map: Record<NonNullable<Props["fontSize"]>, string> = {
    small: "text-[15px]",
    standard: "text-[17px]",
    large: "text-[19px]",
    xlarge: "text-[21px]",
    xxlarge: "text-[23px]",
    max: "text-[25px]",
  };
  return map[size];
}

function optionSpacingClass(spacing: NonNullable<Props["optionSpacing"]>): string {
  const map: Record<NonNullable<Props["optionSpacing"]>, string> = {
    standard: "space-y-4",
    wide: "space-y-6",
    xwide: "space-y-8",
  };
  return map[spacing];
}
